import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import url from '../constants/config';
import ConfirmModal from '../shared/displayComponents/confirmModal/ConfirmModal';
import Loader from '../shared/displayComponents/loader/Loader';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const STEP_TITLES = {
  1: 'START REKRUTACJI',
  2: 'PO AKCEPTACJI UCZELNI',
  3: 'PRZYGOTOWANIE DO WYLOTU',
};

const PRIORITY_OPTIONS = [
  { value: '', label: 'Brak' },
  { value: 'high', label: 'Wysoki' },
  { value: 'medium', label: 'Normalny' },
  { value: 'low', label: 'Niski' },
];

const TASK_OWNER_OPTIONS = [
  { value: '', label: 'Brak' },
  { value: 'athlete', label: 'Zawodnik' },
  { value: 'admin', label: 'Agencja' },
];


const selectStyle = {
  fontSize: '12px',
  padding: '3px 6px',
  border: '1px solid #cbd5e1',
  borderRadius: '4px',
  background: '#f8fafc',
};

const AdminChecklistTemplate = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [toast, setToast] = useState(null);

  // Inline name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  // Task editing
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({});

  // Add task forms per step
  const [addForm, setAddForm] = useState({
    step: 1, label: '', athleteVisible: true, priority: '', taskOwner: '', requires: [],
  });
  const [adding, setAdding] = useState(false);

  // Create template modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [creating, setCreating] = useState(false);

  // Apply modal
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyMode, setApplyMode] = useState('all'); // 'all' | 'select'
  const [playerList, setPlayerList] = useState([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [applying, setApplying] = useState(false);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, confirmLabel: 'Tak', icon: 'fas fa-exclamation-triangle' });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, show: false }));

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${url}/checklist-templates`, { headers: getAuthHeader() });
      const list = res.data.templates || [];
      setTemplates(list);
      if (list.length > 0 && !selectedTemplateId) {
        setSelectedTemplateId(list[0]._id);
      }
    } catch {
      showToast('Błąd ładowania szablonów', 'failed');
    } finally {
      setLoadingTemplates(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTasks = useCallback(async (templateId) => {
    if (!templateId) return;
    setLoadingTasks(true);
    try {
      const res = await axios.get(`${url}/checklist-templates/${templateId}/tasks`, { headers: getAuthHeader() });
      setTasks(res.data.tasks || []);
    } catch {
      showToast('Błąd ładowania zadań', 'failed');
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedTemplateId) {
      const t = templates.find(x => x._id === selectedTemplateId);
      setNameValue(t ? t.name : '');
      loadTasks(selectedTemplateId);
    }
  }, [selectedTemplateId, loadTasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectTemplate = (id) => {
    setSelectedTemplateId(id);
    setEditingTaskId(null);
  };

  // ─── Template CRUD ───────────────────────────────────────────────────────────

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(
        `${url}/checklist-templates`,
        { name: newTemplateName.trim() },
        { headers: getAuthHeader() }
      );
      const created = res.data.template;
      setTemplates(prev => [...prev, created]);
      setSelectedTemplateId(created._id);
      setShowCreateModal(false);
      setNewTemplateName('');
      showToast('Szablon utworzony');
    } catch {
      showToast('Błąd tworzenia szablonu', 'failed');
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (sourceId) => {
    const source = templates.find(t => t._id === sourceId);
    const dupName = source ? `Kopia: ${source.name}` : 'Kopia szablonu';
    try {
      const res = await axios.post(
        `${url}/checklist-templates`,
        { name: dupName, duplicateFrom: sourceId },
        { headers: getAuthHeader() }
      );
      const created = res.data.template;
      setTemplates(prev => [...prev, created]);
      setSelectedTemplateId(created._id);
      showToast('Szablon zduplikowany');
    } catch {
      showToast('Błąd duplikowania', 'failed');
    }
  };

  const handleDeleteTemplate = (id) => {
    if (templates.length <= 1) {
      showToast('Nie można usunąć ostatniego szablonu', 'failed');
      return;
    }
    setConfirmModal({
      show: true,
      title: 'Usunąć szablon?',
      message: 'Szablon i wszystkie jego zadania zostaną trwale usunięte.',
      confirmLabel: 'Usuń szablon',
      icon: 'fas fa-trash-alt',
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(`${url}/checklist-templates/${id}`, { headers: getAuthHeader() });
          const remaining = templates.filter(t => t._id !== id);
          setTemplates(remaining);
          if (selectedTemplateId === id && remaining.length > 0) {
            setSelectedTemplateId(remaining[0]._id);
          }
          showToast('Szablon usunięty');
        } catch {
          showToast('Błąd usuwania', 'failed');
        }
      },
    });
  };

  const handleNameBlur = async () => {
    setEditingName(false);
    const current = templates.find(t => t._id === selectedTemplateId);
    if (!current || nameValue.trim() === current.name) return;
    if (!nameValue.trim()) { setNameValue(current.name); return; }
    try {
      const res = await axios.patch(
        `${url}/checklist-templates/${selectedTemplateId}`,
        { name: nameValue.trim() },
        { headers: getAuthHeader() }
      );
      setTemplates(prev => prev.map(t => t._id === selectedTemplateId ? res.data.template : t));
      showToast('Zapisano nazwę');
    } catch {
      showToast('Błąd zapisu nazwy', 'failed');
      setNameValue(current.name);
    }
  };

  // ─── Task CRUD ───────────────────────────────────────────────────────────────

  const handleAddTask = async () => {
    if (!addForm.label.trim() || !selectedTemplateId) return;
    setAdding(true);
    try {
      const payload = {
        step: Number(addForm.step),
        label: addForm.label.trim(),
        athleteVisible: addForm.athleteVisible,
        ...(addForm.priority && { priority: addForm.priority }),
        ...(addForm.taskOwner && { taskOwner: addForm.taskOwner }),
        ...(addForm.requires.length > 0 && { requires: addForm.requires }),
      };
      const res = await axios.post(
        `${url}/checklist-templates/${selectedTemplateId}/tasks`,
        payload,
        { headers: getAuthHeader() }
      );
      setTasks(prev => [...prev, res.data.task]);
      setAddForm({ step: addForm.step, label: '', athleteVisible: true, priority: '', taskOwner: '', requires: [] });
      showToast('Zadanie dodane do szablonu');
    } catch {
      showToast('Błąd dodawania zadania', 'failed');
    } finally {
      setAdding(false);
    }
  };

  const startEditTask = (t) => {
    setEditingTaskId(t._id);
    setEditTaskForm({
      label: t.label,
      athleteVisible: t.athleteVisible,
      priority: t.priority || '',
      taskOwner: t.taskOwner || '',
      requires: Array.isArray(t.requires) ? [...t.requires] : [],
    });
  };

  const cancelEditTask = () => { setEditingTaskId(null); setEditTaskForm({}); };

  const saveEditTask = async (taskId) => {
    try {
      const payload = {
        label: editTaskForm.label,
        athleteVisible: editTaskForm.athleteVisible,
        priority: editTaskForm.priority || null,
        taskOwner: editTaskForm.taskOwner || null,
        requires: editTaskForm.requires || [],
      };
      const res = await axios.patch(
        `${url}/checklist-templates/${selectedTemplateId}/tasks/${taskId}`,
        payload,
        { headers: getAuthHeader() }
      );
      setTasks(prev => prev.map(t => t._id === taskId ? res.data.task : t));
      setEditingTaskId(null);
      showToast('Zadanie zapisane');
    } catch {
      showToast('Błąd zapisu zadania', 'failed');
    }
  };

  const handleDeleteTask = (taskId) => {
    setConfirmModal({
      show: true,
      title: 'Usunąć zadanie?',
      message: 'Zadanie zostanie trwale usunięte z szablonu.',
      confirmLabel: 'Usuń zadanie',
      icon: 'fas fa-trash-alt',
      onConfirm: async () => {
        closeConfirm();
        try {
          await axios.delete(
            `${url}/checklist-templates/${selectedTemplateId}/tasks/${taskId}`,
            { headers: getAuthHeader() }
          );
          setTasks(prev => prev.filter(t => t._id !== taskId));
          showToast('Zadanie usunięte');
        } catch {
          showToast('Błąd usuwania zadania', 'failed');
        }
      },
    });
  };

  // ─── Drag & Drop ────────────────────────────────────────────────────────────

  const onDragEnd = useCallback(async (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const sourceStep = Number(source.droppableId);
    const destStep = Number(destination.droppableId);

    // Build new tasks array with updated step/order
    const tasksByStep = { 1: [], 2: [], 3: [] };
    tasks.forEach(t => {
      tasksByStep[t.step] = tasksByStep[t.step] || [];
      tasksByStep[t.step].push(t);
    });
    // Sort each step by order
    [1, 2, 3].forEach(s => tasksByStep[s].sort((a, b) => a.order - b.order));

    // Remove dragged task from source
    const [movedTask] = tasksByStep[sourceStep].splice(source.index, 1);
    // Insert into destination
    const updatedTask = { ...movedTask, step: destStep };
    tasksByStep[destStep].splice(destination.index, 0, updatedTask);

    // Reassign order within affected steps
    const reorderPayload = [];
    const newTasks = [];
    [1, 2, 3].forEach(s => {
      tasksByStep[s].forEach((t, idx) => {
        const updated = { ...t, step: s, order: idx + 1 };
        newTasks.push(updated);
        if (s === sourceStep || s === destStep) {
          reorderPayload.push({ _id: t._id, step: s, order: idx + 1 });
        }
      });
    });

    // Optimistic update
    setTasks(newTasks);

    try {
      const res = await axios.patch(
        `${url}/checklist-templates/${selectedTemplateId}/tasks/reorder`,
        { tasks: reorderPayload },
        { headers: getAuthHeader() }
      );
      setTasks(res.data.tasks || newTasks);
    } catch {
      showToast('Błąd zmiany kolejności', 'failed');
      loadTasks(selectedTemplateId);
    }
  }, [tasks, selectedTemplateId, loadTasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Apply Modal ─────────────────────────────────────────────────────────────

  const openApplyModal = async () => {
    setShowApplyModal(true);
    setApplyMode('all');
    setSelectedPlayerIds([]);
    setPlayerSearch('');
    setLoadingPlayers(true);
    try {
      const res = await axios.get(`${url}/checklist-templates/player-list`, { headers: getAuthHeader() });
      setPlayerList(res.data.players || []);
    } catch {
      showToast('Błąd ładowania listy zawodników', 'failed');
    } finally {
      setLoadingPlayers(false);
    }
  };

  const handleApply = () => {
    setConfirmModal({
      show: true,
      title: 'Zastosować szablon?',
      message: 'Spowoduje to usunięcie całego postępu wybranych zawodników (zadania, komentarze, pliki). Tej operacji nie można cofnąć.',
      confirmLabel: 'Zastosuj',
      icon: 'fas fa-exclamation-triangle',
      onConfirm: async () => {
        closeConfirm();
        setApplying(true);
        try {
          const payload = { playerIds: applyMode === 'all' ? 'all' : selectedPlayerIds };
          const res = await axios.post(
            `${url}/checklist-templates/${selectedTemplateId}/apply`,
            payload,
            { headers: getAuthHeader() }
          );
          setShowApplyModal(false);
          showToast(`Zastosowano szablon dla ${res.data.appliedToCount} zawodników`);
        } catch {
          showToast('Błąd stosowania szablonu', 'failed');
        } finally {
          setApplying(false);
        }
      },
    });
  };

  const togglePlayerSelection = (pid) => {
    setSelectedPlayerIds(prev =>
      prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]
    );
  };

  const filteredPlayers = playerList.filter(p =>
    p.name.toLowerCase().includes(playerSearch.toLowerCase())
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  const selectedTemplate = templates.find(t => t._id === selectedTemplateId);

  return (
    <div style={{ padding: '24px 0' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          background: toast.type === 'failed' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'failed' ? '#b91c1c' : '#15803d',
          border: `1px solid ${toast.type === 'failed' ? '#fca5a5' : '#86efac'}`,
          borderRadius: '8px', padding: '12px 20px', fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <Link to="/admin/dashboard" style={{ color: '#2e496b', fontSize: '14px', textDecoration: 'none' }}>
          ← Powrót do panelu
        </Link>
        <h2 style={{ color: '#1e293b', fontWeight: '700', margin: 0, fontSize: '22px' }}>
          <i className="fas fa-layer-group" style={{ marginRight: '10px', color: '#2e496b' }} />
          Szablony checklisty
        </h2>
      </div>

      {loadingTemplates ? (
        <Loader />
      ) : (
        <div style={{ display: 'flex', gap: '0', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', minHeight: '500px' }}>

          {/* ─── Left sidebar ───────────────────────────────────────── */}
          <div style={{ width: '240px', flexShrink: 0, background: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Szablony
              </span>
              <button
                type="button"
                onClick={() => { setShowCreateModal(true); setNewTemplateName(''); }}
                style={{ background: '#2e496b', color: '#fff', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer' }}
                title="Nowy szablon"
              >
                + Nowy
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
              {templates.map(t => (
                <div
                  key={t._id}
                  style={{
                    borderRadius: '6px',
                    marginBottom: '4px',
                    background: t._id === selectedTemplateId ? '#2e496b' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 10px' }}
                  >
                    <span
                      onClick={() => handleSelectTemplate(t._id)}
                      style={{
                        flex: 1, fontSize: '13px', fontWeight: t._id === selectedTemplateId ? '600' : '400',
                        color: t._id === selectedTemplateId ? '#fff' : '#334155',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                      title={t.name}
                    >
                      {t.name}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDuplicate(t._id); }}
                      title="Duplikuj szablon"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px',
                        color: t._id === selectedTemplateId ? '#bfdbfe' : '#94a3b8', fontSize: '11px',
                      }}
                    >
                      <i className="fas fa-copy" />
                    </button>
                    {templates.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t._id); }}
                        title="Usuń szablon"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px',
                          color: t._id === selectedTemplateId ? '#fca5a5' : '#e53e3e', fontSize: '11px',
                        }}
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Right panel ─────────────────────────────────────────── */}
          <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
            {!selectedTemplate ? (
              <p style={{ color: '#94a3b8' }}>Wybierz szablon z listy po lewej.</p>
            ) : (
              <>
                {/* Template name + Apply button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  {editingName ? (
                    <input
                      type="text"
                      value={nameValue}
                      autoFocus
                      onChange={e => setNameValue(e.target.value)}
                      onBlur={handleNameBlur}
                      onKeyDown={e => { if (e.key === 'Enter') handleNameBlur(); if (e.key === 'Escape') { setEditingName(false); setNameValue(selectedTemplate.name); } }}
                      style={{ fontSize: '18px', fontWeight: '700', border: '1px solid #2e496b', borderRadius: '4px', padding: '3px 8px', color: '#1e293b' }}
                    />
                  ) : (
                    <h3
                      onClick={() => setEditingName(true)}
                      style={{ color: '#1e293b', fontWeight: '700', margin: 0, fontSize: '18px', cursor: 'pointer', borderBottom: '1px dashed #cbd5e1' }}
                      title="Kliknij, aby edytować nazwę"
                    >
                      {selectedTemplate.name}
                    </h3>
                  )}
                  <button
                    type="button"
                    onClick={openApplyModal}
                    style={{
                      background: '#2e496b', color: '#fff', border: 'none', borderRadius: '6px',
                      padding: '7px 16px', fontSize: '13px', cursor: 'pointer',
                    }}
                  >
                    <i className="fas fa-play-circle" style={{ marginRight: '7px' }} />
                    Zastosuj do zawodników
                  </button>
                </div>

                {/* Info banner */}
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', color: '#1e40af', marginBottom: '20px' }}>
                  <i className="fas fa-info-circle" style={{ marginRight: '8px' }} />
                  Edycja szablonu <strong>nie zmienia</strong> istniejących zadań zawodników. Użyj &ldquo;Zastosuj do zawodników&rdquo;, aby zasiać od nowa.
                </div>

                {/* Add task form */}
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px', marginBottom: '24px' }}>
                  <h4 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>
                    <i className="fas fa-plus-circle" style={{ marginRight: '7px', color: '#2e496b' }} />
                    Dodaj zadanie do szablonu
                  </h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>Krok</div>
                      <select value={addForm.step} onChange={e => { const v = Number(e.target.value); setAddForm(f => ({ ...f, step: v })); }} style={selectStyle}>
                        {[1, 2, 3].map(s => <option key={s} value={s}>Krok {s}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>Nazwa zadania *</div>
                      <input
                        type="text"
                        placeholder="Nazwa zadania..."
                        value={addForm.label}
                        onChange={e => { const v = e.target.value; setAddForm(f => ({ ...f, label: v })); }}
                        onKeyDown={e => e.key === 'Enter' && handleAddTask()}
                        style={{ width: '100%', fontSize: '13px', padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>Priorytet</div>
                      <select value={addForm.priority} onChange={e => { const v = e.target.value; setAddForm(f => ({ ...f, priority: v })); }} style={selectStyle}>
                        {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>Właściciel</div>
                      <select value={addForm.taskOwner} onChange={e => { const v = e.target.value; setAddForm(f => ({ ...f, taskOwner: v })); }} style={selectStyle}>
                        {TASK_OWNER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>Widoczność</div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={addForm.athleteVisible} onChange={e => { const checked = e.target.checked; setAddForm(f => ({ ...f, athleteVisible: checked })); }} />
                        Zawodnik
                      </label>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>Wymaga</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[{ val: 'file', lbl: 'Plik' }, { val: 'text', lbl: 'Tekst' }, { val: 'link', lbl: 'Link' }].map(({ val, lbl }) => (
                          <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={addForm.requires.includes(val)}
                              onChange={e => { const checked = e.target.checked; setAddForm(f => ({
                                ...f,
                                requires: checked ? [...f.requires, val] : f.requires.filter(r => r !== val),
                              })); }}
                            />
                            {lbl}
                          </label>
                        ))}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddTask}
                      disabled={!addForm.label.trim() || adding}
                      style={{
                        background: '#2e496b', color: '#fff', border: 'none', borderRadius: '4px',
                        padding: '6px 16px', fontSize: '13px',
                        cursor: addForm.label.trim() && !adding ? 'pointer' : 'not-allowed',
                        opacity: addForm.label.trim() && !adding ? 1 : 0.5,
                      }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '6px' }} />
                      {adding ? 'Dodawanie...' : 'Dodaj'}
                    </button>
                  </div>
                </div>

                {/* Task list by step */}
                {loadingTasks ? (
                  <p style={{ color: '#64748b' }}>Ładowanie zadań...</p>
                ) : (
                  <DragDropContext onDragEnd={onDragEnd}>
                    {[1, 2, 3].map(step => {
                      const stepTasks = tasks.filter(t => t.step === step);
                      return (
                        <div key={step} style={{ marginBottom: '28px' }}>
                          <div style={{
                            background: '#2e496b', color: '#fff', padding: '10px 16px',
                            borderRadius: '8px 8px 0 0', fontWeight: '600', fontSize: '14px',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          }}>
                            <span>Krok {step}: {STEP_TITLES[step]}</span>
                            <span style={{ fontSize: '12px', opacity: 0.85 }}>{stepTasks.length} zadań</span>
                          </div>
                          <Droppable droppableId={String(step)}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                  border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px',
                                  minHeight: '40px',
                                  background: snapshot.isDraggingOver ? '#f0f9ff' : 'transparent',
                                }}
                              >
                                {stepTasks.length === 0 && !snapshot.isDraggingOver && (
                                  <div style={{ padding: '14px', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>
                                    Brak zadań w tym kroku.
                                  </div>
                                )}
                                {stepTasks.map((t, idx) => (
                                  <Draggable key={t._id} draggableId={t._id} index={idx}>
                                    {(dragProvided, dragSnapshot) => (
                                      <div
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        style={{
                                          ...dragProvided.draggableProps.style,
                                          padding: '10px 14px',
                                          borderBottom: idx < stepTasks.length - 1 ? '1px solid #f1f5f9' : 'none',
                                          background: dragSnapshot.isDragging ? '#e0f2fe' : (t.athleteVisible ? '#fff' : '#fef9f0'),
                                          boxShadow: dragSnapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                                        }}
                                      >
                                        {editingTaskId === t._id ? (
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <input
                                              type="text"
                                              value={editTaskForm.label}
                                              onChange={e => { const v = e.target.value; setEditTaskForm(f => ({ ...f, label: v })); }}
                                              style={{ fontSize: '13px', padding: '5px 10px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                                            />
                                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                              <select value={editTaskForm.priority} onChange={e => { const v = e.target.value; setEditTaskForm(f => ({ ...f, priority: v })); }} style={selectStyle}>
                                                {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                              </select>
                                              <select value={editTaskForm.taskOwner} onChange={e => { const v = e.target.value; setEditTaskForm(f => ({ ...f, taskOwner: v })); }} style={selectStyle}>
                                                {TASK_OWNER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                              </select>
                                              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer' }}>
                                                <input type="checkbox" checked={editTaskForm.athleteVisible} onChange={e => { const checked = e.target.checked; setEditTaskForm(f => ({ ...f, athleteVisible: checked })); }} />
                                                Widoczne dla zawodnika
                                              </label>
                                              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Wymaga:</span>
                                              {[{ val: 'file', lbl: 'Plik' }, { val: 'text', lbl: 'Tekst' }, { val: 'link', lbl: 'Link' }].map(({ val, lbl }) => (
                                                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', cursor: 'pointer' }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={(editTaskForm.requires || []).includes(val)}
                                                    onChange={e => { const checked = e.target.checked; setEditTaskForm(f => ({
                                                      ...f,
                                                      requires: checked ? [...(f.requires || []), val] : (f.requires || []).filter(r => r !== val),
                                                    })); }}
                                                  />
                                                  {lbl}
                                                </label>
                                              ))}
                                              <button type="button" onClick={() => saveEditTask(t._id)} style={{ background: '#2e496b', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 14px', fontSize: '12px', cursor: 'pointer' }}>Zapisz</button>
                                              <button type="button" onClick={cancelEditTask} style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer', color: '#475569' }}>Anuluj</button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            <span {...dragProvided.dragHandleProps} style={{ cursor: 'grab', color: '#94a3b8', fontSize: '13px', display: 'flex', alignItems: 'center' }} title="Przeciągnij, aby zmienić kolejność">
                                              <i className="fas fa-grip-vertical" />
                                            </span>
                                            <span style={{ flex: 1, fontSize: '14px', color: '#334155', minWidth: '160px' }}>{t.label}</span>
                                            {!t.athleteVisible && (
                                              <span style={{ fontSize: '11px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 6px' }}>
                                                <span role="img" aria-label="ukryte">🔒</span> Admin
                                              </span>
                                            )}
                                            {t.priority && (
                                              <span style={{ fontSize: '11px', background: t.priority === 'high' ? '#fee2e2' : t.priority === 'medium' ? '#fef3c7' : '#dbeafe', color: t.priority === 'high' ? '#b91c1c' : t.priority === 'medium' ? '#92400e' : '#1e40af', borderRadius: '4px', padding: '1px 7px' }}>
                                                {t.priority === 'high' ? 'Wysoki' : t.priority === 'medium' ? 'Normalny' : 'Niski'}
                                              </span>
                                            )}
                                            {t.taskOwner && (
                                              <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                {t.taskOwner === 'athlete' ? '👤 Zawodnik' : '🏢 Agencja'}
                                              </span>
                                            )}
                                            {Array.isArray(t.requires) && t.requires.length > 0 && (
                                              <span style={{ display: 'inline-flex', gap: '3px' }}>
                                                {t.requires.includes('file') && (
                                                  <span style={{ fontSize: '10px', background: '#dbeafe', color: '#1e40af', borderRadius: '4px', padding: '1px 5px' }}>
                                                    <i className="fas fa-paperclip" style={{ marginRight: '2px', fontSize: '9px' }} />Plik
                                                  </span>
                                                )}
                                                {t.requires.includes('text') && (
                                                  <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 5px' }}>
                                                    <i className="fas fa-pen" style={{ marginRight: '2px', fontSize: '9px' }} />Tekst
                                                  </span>
                                                )}
                                                {t.requires.includes('link') && (
                                                  <span style={{ fontSize: '10px', background: '#ede9fe', color: '#6d28d9', borderRadius: '4px', padding: '1px 5px' }}>
                                                    <i className="fas fa-link" style={{ marginRight: '2px', fontSize: '9px' }} />Link
                                                  </span>
                                                )}
                                              </span>
                                            )}
                                            <button type="button" onClick={() => startEditTask(t)} style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 10px', fontSize: '12px', cursor: 'pointer', color: '#2e496b' }}>
                                              <i className="fas fa-edit" />
                                            </button>
                                            <button type="button" onClick={() => handleDeleteTask(t._id)} style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '13px', padding: '0 4px' }} title="Usuń">
                                              <i className="fas fa-trash-alt" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      );
                    })}
                  </DragDropContext>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Create template modal ─────────────────────────────────────────── */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '10px', padding: '28px', width: '380px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1e293b' }}>Nowy szablon</h3>
            <input
              type="text"
              placeholder="Nazwa szablonu..."
              value={newTemplateName}
              onChange={e => setNewTemplateName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateTemplate()}
              autoFocus
              style={{ width: '100%', fontSize: '14px', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', marginBottom: '16px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '7px 18px', fontSize: '13px', cursor: 'pointer', color: '#475569' }}>Anuluj</button>
              <button type="button" onClick={handleCreateTemplate} disabled={!newTemplateName.trim() || creating} style={{ background: '#2e496b', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 18px', fontSize: '13px', cursor: newTemplateName.trim() && !creating ? 'pointer' : 'not-allowed', opacity: newTemplateName.trim() && !creating ? 1 : 0.5 }}>
                {creating ? 'Tworzenie...' : 'Utwórz'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Apply modal ──────────────────────────────────────────────────── */}
      {showApplyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '10px', padding: '28px', width: '480px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1e293b' }}>
              Zastosuj szablon: <em style={{ fontStyle: 'normal', color: '#2e496b' }}>{selectedTemplate?.name}</em>
            </h3>

            {/* Warning */}
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#9a3412', marginBottom: '16px' }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }} />
              Spowoduje to usunięcie całego postępu wybranych zawodników (zadania, komentarze, pliki).
            </div>

            {/* Mode selection */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              {[{ val: 'all', label: 'Wszyscy zawodnicy' }, { val: 'select', label: 'Wybierz zawodników' }].map(({ val, label }) => (
                <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                  <input type="radio" name="applyMode" value={val} checked={applyMode === val} onChange={() => setApplyMode(val)} />
                  {label}
                </label>
              ))}
            </div>

            {/* Player selector */}
            {applyMode === 'select' && (
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px' }}>
                <input
                  type="text"
                  placeholder="Szukaj zawodnika..."
                  value={playerSearch}
                  onChange={e => setPlayerSearch(e.target.value)}
                  style={{ width: '100%', fontSize: '13px', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', marginBottom: '10px', boxSizing: 'border-box' }}
                />
                {loadingPlayers ? (
                  <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>Ładowanie...</p>
                ) : filteredPlayers.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>Brak zawodników</p>
                ) : (
                  filteredPlayers.map(p => (
                    <label key={p.linkedPlayerId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}>
                      <input
                        type="checkbox"
                        checked={selectedPlayerIds.includes(p.linkedPlayerId)}
                        onChange={() => togglePlayerSelection(p.linkedPlayerId)}
                      />
                      <span style={{ flex: 1 }}>{p.name}</span>
                      {p.templateName && (
                        <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', borderRadius: '4px', padding: '1px 6px' }}>
                          {p.templateName}
                        </span>
                      )}
                    </label>
                  ))
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button type="button" onClick={() => setShowApplyModal(false)} style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '7px 18px', fontSize: '13px', cursor: 'pointer', color: '#475569' }}>Anuluj</button>
              <button
                type="button"
                onClick={handleApply}
                disabled={applying || (applyMode === 'select' && selectedPlayerIds.length === 0)}
                style={{
                  background: '#2e496b', color: '#fff', border: 'none', borderRadius: '6px',
                  padding: '7px 18px', fontSize: '13px',
                  cursor: !applying && (applyMode === 'all' || selectedPlayerIds.length > 0) ? 'pointer' : 'not-allowed',
                  opacity: !applying && (applyMode === 'all' || selectedPlayerIds.length > 0) ? 1 : 0.5,
                }}
              >
                {applying ? 'Stosowanie...' : 'Zastosuj'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm || (() => {})}
        onCancel={closeConfirm}
        confirmLabel={confirmModal.confirmLabel || 'Tak'}
        icon={confirmModal.icon || 'fas fa-exclamation-triangle'}
        danger
      />
    </div>
  );
};

export default AdminChecklistTemplate;
